import {
	faBolt,
	faBoxes,
	faCar,
	faCarBattery,
	faCarCrash,
	faClipboardCheck,
	faCloudShowersHeavy,
	faCogs,
	faCompactDisc,
	faCrosshairs,
	faDotCircle,
	faFire,
	faGasPump,
	faHammer,
	faKey,
	faOilCan,
	faRoute,
	faSnowflake,
	faSprayCan,
	faStethoscope,
	faUserSecret,
	faWrench,
} from "@fortawesome/free-solid-svg-icons"
import { COLORS } from "../../utilities/Constants"

// Écran salle de pause : le thème de l'app (V2) porté à l'échelle d'un écran
// lu à 3-4 m. Une seule grammaire visuelle : chaque chiffre court contre son
// propre record, sur une barre qui se remplit (cf. `Barre` dans EcranSlides).
export const T = {
	fond: COLORS.bg,
	surface: COLORS.surface,
	trait: COLORS.border,
	// Fond de barre : ce qu'il reste à faire pour égaler le record.
	fantome: COLORS.borderStrong,
	encre: COLORS.text,
	discret: COLORS.textMuted,
	// Aujourd'hui (barre et texte « dont X aujourd'hui »).
	bleu: COLORS.accent,
	bleuFond: COLORS.accentLight,
	// Record (trophée, barre au-delà de l'ancien record).
	or: COLORS.gold,
	vert: COLORS.success,
	orange: COLORS.warning,
	orangeFond: COLORS.warningLight,
	typo: "'DM Sans', 'Helvetica Neue', Helvetica, Arial, sans-serif",
}

// Tailles proportionnelles à la hauteur d'écran, avec des planchers lisibles
// sur une petite tablette.
export const TAILLE = {
	heros: "clamp(110px, 20vh, 250px)",
	moyen: "clamp(64px, 11vh, 140px)",
	chiffre: "clamp(40px, 6.2vh, 80px)",
	titre: "clamp(30px, 4.6vh, 60px)",
	grand: "clamp(26px, 3.7vh, 48px)",
	texte: "clamp(22px, 3vh, 38px)",
	petit: "clamp(18px, 2.4vh, 30px)",
}

// Pas de fête pour un « record » de 1 ou 2 (début d'historique, travaux
// rares) : la phrase reste neutre et rien ne passe en or.
export const SEUIL_FETE = 3

export const nombre = (n) =>
	n === null || n === undefined ? "—" : Number(n).toLocaleString("fr-CH")

export const pluriel = (n, un, plusieurs) => (n > 1 ? plusieurs : un)

// Noms écrits en dur : le rendu ne dépend pas des données de langue du
// navigateur de la tablette.
const JOURS = [
	"dimanche",
	"lundi",
	"mardi",
	"mercredi",
	"jeudi",
	"vendredi",
	"samedi",
]
const MOIS = [
	"janvier",
	"février",
	"mars",
	"avril",
	"mai",
	"juin",
	"juillet",
	"août",
	"septembre",
	"octobre",
	"novembre",
	"décembre",
]

const jourDuMois = (d) => (d.getDate() === 1 ? "1er" : String(d.getDate()))

// "2026-07-27" → Date locale (sans décalage de fuseau).
const dateIso = (iso) => {
	const [a, m, j] = iso.split("-").map(Number)
	return new Date(a, m - 1, j)
}

// « 27 juillet »
export const jourMois = (iso) => {
	if (!iso) return ""
	const d = dateIso(iso)
	return `${jourDuMois(d)} ${MOIS[d.getMonth()]}`
}

// « Mercredi 1er octobre »
export const dateDuJour = (d) => {
	const s = `${JOURS[d.getDay()]} ${jourDuMois(d)} ${MOIS[d.getMonth()]}`
	return s.charAt(0).toUpperCase() + s.slice(1)
}

export const heureMinute = (d) =>
	`${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`

// ─── Course au record ──────────────────────────────────────────────────────

// `precedent` = meilleure période AVANT celle en cours (0 = pas encore de
// record à battre).
export const course = (actuel, precedent) => {
	const p = precedent || 0
	if (!p) return { precedent: 0, etat: "premier", fete: false }
	const etat = actuel > p ? "battu" : actuel === p ? "egale" : "en_course"
	return {
		precedent: p,
		etat,
		fete: etat !== "en_course" && actuel >= SEUIL_FETE,
	}
}

// La phrase qui dit où en est la course, en clair.
export const phraseCourse = (actuel, c) => {
	if (c.etat === "premier") return null
	if (c.etat === "en_course")
		return `Encore ${nombre(c.precedent - actuel)} pour égaler le record.`
	if (c.etat === "egale")
		return c.fete
			? "Record égalé ! Encore 1 pour le battre."
			: "Encore 1 pour battre le record."
	// Battu : l'ancien record est déjà affiché à côté du chiffre.
	return c.fete ? "Nouveau record !" : "Record battu."
}

// ─── Icônes ────────────────────────────────────────────────────────────────

const premiereIcone = (table, nom) => {
	const trouve = table.find(([motif]) => motif.test(nom || ""))
	return trouve ? trouve[1] : faWrench
}

// Modèles de travaux de l'atelier (noms éditables : on lit le libellé).
// L'ordre compte : « Vidange carburant » doit trouver la pompe avant l'huile.
const ICONES_TRAVAUX = [
	[/carburant/i, faGasPump],
	[/pneu|roue|tubless|tubeless|jante|équilibr/i, faDotCircle],
	[/service|vidange|huile/i, faOilCan],
	[/clim/i, faSnowflake],
	[/diagnost|valise/i, faStethoscope],
	[/visite|expertise|contrôle|controle/i, faClipboardCheck],
	[/géométrie|geometrie|parallél/i, faCrosshairs],
	[/batter/i, faCarBattery],
	[/frein|plaquette|disque/i, faCompactDisc],
	[/carross|peinture/i, faSprayCan],
	[/inventaire/i, faBoxes],
	[/grue|graiss/i, faCogs],
]

export const iconeTravaux = (nom) => premiereIcone(ICONES_TRAVAUX, nom)

// Catégories de diagnostic du dépannage (arbre éditable : libellé, jamais
// d'id en dur).
const ICONES_DIAG = [
	[/batter/i, faCarBattery],
	[/pneu|crevais/i, faDotCircle],
	[/collision|accident/i, faCarCrash],
	[/incendi|feu/i, faFire],
	[/sortie|embourb/i, faRoute],
	[/^vol/i, faUserSecret],
	[/vandal/i, faHammer],
	[/naturel|grêle|inond/i, faCloudShowersHeavy],
	[/moteur|électri|electri/i, faBolt],
	[/embray|transmi|boîte|boite/i, faCogs],
	[/clé|cle\b|contact/i, faKey],
	[/châssis|chassis|carross/i, faCar],
	[/carbur|adblue|ad-blue/i, faGasPump],
]

export const iconeDiag = (nom) => premiereIcone(ICONES_DIAG, nom)
